import type { ReactNode } from "react";

import { Label } from "src/components/ui/label";
import { cn } from "src/lib/utils";

export interface FieldProps {
	/** Visible label. Omit for an unlabelled control. */
	label?: ReactNode;
	/** Bound to the control's `id`/`htmlFor`, and to the Formik field name. */
	name?: string;
	/** Renders the red asterisk. Validation itself stays with the schema. */
	required?: boolean;
	/** Validation message. Anything falsy renders nothing. */
	error?: ReactNode;
	/** Helper copy under the control, hidden while an error is showing. */
	hint?: ReactNode;
	className?: string;
	children?: ReactNode;
}

/**
 * Label + control + error/hint wrapper — the single layout for every form row in
 * the redesign. Replaces the legacy `.formItem` / `.errorMsg` CSS from `index.css`.
 */
export const Field = ({ label, name, required, error, hint, className, children }: FieldProps) => (
	<div className={cn("tw-grid tw-gap-1.5", className)}>
		{!!label && (
			<Label htmlFor={name}>
				{label}
				{required && <span className="tw-ml-0.5 tw-text-red"> *</span>}
			</Label>
		)}

		{children}

		{error ? (
			<p className="tw-text-[12px] tw-text-red">{error}</p>
		) : (
			!!hint && <p className="tw-text-[12px] tw-text-text-soft">{hint}</p>
		)}
	</div>
);

export default Field;
